import { AlertCircle, CheckCircle2, Download, FileText, Upload } from "lucide-react";
import { unzipSync } from "fflate";
import { useMemo, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import "./index.css";
import { generateCertificatePdf, parseCertificateXml, parseSignatureCertificateSerial } from "./certificatePdf";
import type { CertificateData, SignatureInfo } from "./certificatePdf";

const APP_VERSION = "0.1.0";

type UploadState =
  | { status: "idle"; signatureInfo?: SignatureInfo; sigFileName?: string }
  | { status: "ready"; fileName: string; data: CertificateData; signatureInfo?: SignatureInfo; sigFileName?: string }
  | { status: "error"; message: string; previous?: Extract<UploadState, { status: "ready" }> };

export function App() {
  const [upload, setUpload] = useState<UploadState>({ status: "idle" });
  const [isGenerating, setIsGenerating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const readyUpload = upload.status === "ready" ? upload : upload.status === "error" ? upload.previous : undefined;

  const summary = useMemo(() => {
    if (!readyUpload) {
      return [];
    }

    return [
      ["Справка", `№${readyUpload.data.certificateNumber}`],
      ["Дата", readyUpload.data.certificateDate],
      ["ИНН", readyUpload.data.inn],
      ["КПП", readyUpload.data.kpp || "—"],
      ["Налоговый орган", `${readyUpload.data.taxOfficeName}, ${readyUpload.data.taxOfficeCode}`],
      ["SIG", readyUpload.sigFileName || "—"],
    ];
  }, [readyUpload]);

  async function readFiles(files: FileList | File[]) {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) {
      return;
    }

    const previousReady = upload.status === "ready" ? upload : upload.status === "error" ? upload.previous : undefined;
    const previousSignatureInfo =
      upload.status === "idle" ? upload.signatureInfo : upload.status === "ready" ? upload.signatureInfo : upload.previous?.signatureInfo;
    const previousSigFileName =
      upload.status === "idle" ? upload.sigFileName : upload.status === "ready" ? upload.sigFileName : upload.previous?.sigFileName;
    let nextReady = previousReady;
    let nextSignatureInfo = previousSignatureInfo;
    let nextSigFileName = previousSigFileName;

    try {
      for (const file of fileArray) {
        const lowerName = file.name.toLowerCase();
        const buffer = await file.arrayBuffer();
        const entries = lowerName.endsWith(".zip") ? unzipUpload(buffer) : [{ name: file.name, buffer }];

        for (const entry of entries) {
          const lowerEntryName = entry.name.toLowerCase();

          if (lowerEntryName.endsWith(".xml")) {
            const text = new TextDecoder("windows-1251").decode(entry.buffer);
            const data = parseCertificateXml(text, entry.name);
            applySignatureInfo(data, nextSignatureInfo);
            nextReady = { status: "ready", fileName: entry.name, data, signatureInfo: nextSignatureInfo, sigFileName: nextSigFileName };
            continue;
          }

          if (lowerEntryName.endsWith(".sig")) {
            nextSignatureInfo = parseSignatureCertificateSerial(entry.buffer);
            nextSigFileName = entry.name;
            if (nextReady) {
              const data = { ...nextReady.data };
              applySignatureInfo(data, nextSignatureInfo);
              nextReady = {
                ...nextReady,
                signatureInfo: nextSignatureInfo,
                sigFileName: nextSigFileName,
                data,
              };
            }
            continue;
          }
        }
      }

      if (!nextReady) {
        setUpload({ status: "idle", signatureInfo: nextSignatureInfo, sigFileName: nextSigFileName });
        return;
      }

      setUpload(nextReady);
    } catch (error) {
      setUpload({
        status: "error",
        previous: nextReady,
        message: error instanceof Error ? error.message : "Не удалось прочитать файл.",
      });
    }
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.currentTarget.files) {
      void readFiles(event.currentTarget.files);
      event.currentTarget.value = "";
    }
  }

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (event.dataTransfer.files) {
      void readFiles(event.dataTransfer.files);
    }
  }

  async function handleDownload() {
    if (!readyUpload) {
      return;
    }

    setIsGenerating(true);
    try {
      const pdfBytes = await generateCertificatePdf(readyUpload.data);
      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${readyUpload.data.fileId || readyUpload.fileName.replace(/\.xml$/i, "")}.pdf`;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <main className="app-shell">
      <div className="app-version">v{APP_VERSION}</div>
      <section className="workspace">
        <div className="panel intro-panel">
          <p className="eyebrow">ФНС XML → PDF</p>
          <h1>Генератор справки</h1>
          <p className="lead">
            Загрузите XML-файл ответа ИОН и, при наличии, SIG-файл подписи. Файлы читаются локально в браузере.
          </p>

          <input
            ref={inputRef}
            className="file-input"
            type="file"
            accept=".xml,.sig,.zip,text/xml,application/zip"
            multiple
            onChange={handleInputChange}
          />
          <button
            className="drop-zone"
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <Upload size={28} aria-hidden="true" />
            <span>Выбрать XML / SIG / ZIP</span>
          </button>

          {upload.status === "error" && (
            <div className="status error">
              <AlertCircle size={18} aria-hidden="true" />
              <span>{upload.message}</span>
            </div>
          )}

          {readyUpload && (
            <div className="status success">
              <CheckCircle2 size={18} aria-hidden="true" />
              <span>
                {readyUpload.fileName}
                {readyUpload.sigFileName ? ` + ${readyUpload.sigFileName}` : ""}
              </span>
            </div>
          )}
        </div>

        <div className="panel details-panel">
          <div className="panel-title">
            <FileText size={20} aria-hidden="true" />
            <h2>Данные справки</h2>
          </div>

          {readyUpload ? (
            <>
              <dl className="details-list">
                {summary.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <button className="download-button" type="button" onClick={handleDownload} disabled={isGenerating}>
                <Download size={20} aria-hidden="true" />
                <span>{isGenerating ? "Формирование..." : "Скачать PDF"}</span>
              </button>
            </>
          ) : (
            <div className="empty-state">Ожидается XML-файл справки.</div>
          )}
        </div>
      </section>
    </main>
  );
}

export default App;

function applySignatureInfo(data: CertificateData, signatureInfo?: SignatureInfo) {
  if (!signatureInfo) {
    return;
  }

  data.certificateSerial = signatureInfo.certificateSerial;
  data.certificateSubject = signatureInfo.subject;
  data.certificateSignerName = signatureInfo.signerName;
  data.certificateSignerPosition = signatureInfo.signerPosition;
}

function unzipUpload(buffer: ArrayBuffer) {
  const files = unzipSync(new Uint8Array(buffer));
  return Object.entries(files)
    .filter(([name]) => !name.endsWith("/"))
    .map(([name, content]) => {
      const copy = new Uint8Array(content.byteLength);
      copy.set(content);
      return { name, buffer: copy.buffer };
    });
}
